import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OtpInput } from '@/components/ui/otp-input';
import { Logo } from '@/components/layout/Logo';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { login, clearError, googleLogin, requestLoginOtp, verifyLoginOtp } from '@/features/auth/authSlice';
import { GoogleLogin } from '@react-oauth/google';
import { useToast } from '@/hooks/use-toast';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const OTP_EXPIRY_SECONDS = 300; // 5 minutes
const RESEND_COOLDOWN_SECONDS = 30;

export default function Login() {
  useDocumentTitle('Login');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  const { toast } = useToast();
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // OTP state
  const [otpStep, setOtpStep] = useState<'email' | 'verify'>('email');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [resendCountdown, setResendCountdown] = useState(0);
  const otpVerifyingRef = useRef(false);

  const redirectTo = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // OTP countdown timer
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const timer = setInterval(() => {
      setOtpCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCountdown]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => {
      setResendCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  // Auto-submit when OTP is complete
  useEffect(() => {
    if (otpValue.length === 6 && !otpVerifyingRef.current) {
      otpVerifyingRef.current = true;
      dispatch(verifyLoginOtp({ email: otpEmail, code: otpValue }))
        .unwrap()
        .then(() => {
          navigate(redirectTo, { replace: true });
        })
        .catch(() => {
          // error handled by Redux
        })
        .finally(() => {
          otpVerifyingRef.current = false;
        });
    }
  }, [otpValue, otpEmail, dispatch, navigate, redirectTo]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dispatch(login({ email, password })).unwrap();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const handleSendOtp = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());
    try {
      await dispatch(requestLoginOtp(otpEmail)).unwrap();
      setOtpStep('verify');
      setOtpValue('');
      setOtpCountdown(OTP_EXPIRY_SECONDS);
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
    } catch {
      // error handled by Redux
    }
  }, [dispatch, otpEmail]);

  const handleResendOtp = useCallback(async () => {
    dispatch(clearError());
    try {
      await dispatch(requestLoginOtp(otpEmail)).unwrap();
      setOtpValue('');
      setOtpCountdown(OTP_EXPIRY_SECONDS);
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      toast({ title: 'OTP resent', description: 'A new code has been sent to your email.' });
    } catch {
      // error handled by Redux
    }
  }, [dispatch, otpEmail, toast]);

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md transition-all duration-200 hover:shadow-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <Logo />
          </div>
          <CardTitle className="text-3xl font-bold tracking-tight">Welcome back</CardTitle>
          <CardDescription>
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* ── Google SSO ─────────────────────────────────────────────── */}
          <div className="mb-4 space-y-3">
            {googleClientId ? (
              <div className="flex justify-center w-full [&>div]:w-full [&_iframe]:!w-full">
                <GoogleLogin
                  onSuccess={async (credentialResponse) => {
                    try {
                      await dispatch(googleLogin({ credential: credentialResponse.credential! })).unwrap();
                      navigate(redirectTo, { replace: true });
                    } catch (e: unknown) {
                      toast({ title: 'Google sign-in failed', description: (e as string) || 'Unable to sign in with Google', variant: 'destructive' });
                    }
                  }}
                  onError={() => toast({ title: 'Google sign-in failed', description: 'The sign-in was cancelled or an error occurred', variant: 'destructive' })}
                  text="signin_with"
                  shape="rectangular"
                />
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full flex items-center gap-3 font-medium"
                onClick={() => toast({
                  title: 'Google Sign-In not configured',
                  description: 'Set VITE_GOOGLE_CLIENT_ID in your environment to enable Google Sign-In.',
                })}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </Button>
            )}
            <div className="relative flex items-center">
              <Separator className="flex-1" />
              <span className="mx-3 text-xs text-muted-foreground">or continue with email</span>
              <Separator className="flex-1" />
            </div>
          </div>

          <Tabs defaultValue="password" onValueChange={() => dispatch(clearError())} data-testid="login-tabs">
            <TabsList className="w-full">
              <TabsTrigger value="password" className="flex-1" data-testid="login-tab-password">Password</TabsTrigger>
              <TabsTrigger value="otp" className="flex-1" data-testid="login-tab-otp">Email OTP</TabsTrigger>
            </TabsList>

            {/* ── Password Tab ──────────────────────────────────────────── */}
            <TabsContent value="password">
              <form onSubmit={handleLogin} className="space-y-4">
                {error && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-md" data-testid="login-error">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="demo@fintrack.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    data-testid="password-input"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-button">
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>

            {/* ── Email OTP Tab ─────────────────────────────────────────── */}
            <TabsContent value="otp">
              {error && (
                <div className="p-3 mb-4 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-md" data-testid="otp-login-error">
                  {error}
                </div>
              )}

              {otpStep === 'email' ? (
                <form onSubmit={handleSendOtp} className="space-y-4" data-testid="otp-login-email-form">
                  <div className="space-y-2">
                    <Label htmlFor="otp-email">Email</Label>
                    <Input
                      id="otp-email"
                      type="email"
                      placeholder="demo@fintrack.com"
                      value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      data-testid="otp-login-email-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading} data-testid="otp-login-send-btn">
                    {isLoading ? 'Sending...' : 'Send OTP'}
                  </Button>
                </form>
              ) : (
                <div className="space-y-4" data-testid="otp-login-verify-form">
                  <p className="text-sm text-muted-foreground text-center">
                    Enter the 6-digit code sent to <span className="font-medium text-foreground">{otpEmail}</span>
                  </p>
                  <OtpInput
                    value={otpValue}
                    onChange={setOtpValue}
                    disabled={isLoading}
                    error={!!error}
                    autoFocus
                  />
                  <div className="text-center text-sm text-muted-foreground">
                    {otpCountdown > 0 ? (
                      <p>Code expires in <span className="font-medium">{formatCountdown(otpCountdown)}</span></p>
                    ) : (
                      <p className="text-destructive">Code expired. Resend code below.</p>
                    )}
                  </div>
                  <div className="text-center">
                    {resendCountdown > 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Resend code (available in {formatCountdown(resendCountdown)})
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={isLoading}
                        className="text-sm text-primary hover:underline underline-offset-4 disabled:opacity-50"
                        data-testid="otp-login-resend-btn"
                      >
                        Resend code
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setOtpStep('email'); setOtpValue(''); dispatch(clearError()); }}
                    className="w-full text-sm text-muted-foreground hover:text-foreground text-center"
                  >
                    &larr; Use a different email
                  </button>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <div className="mt-4 text-center text-sm">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="text-primary hover:underline underline-offset-4"
            >
              Sign up
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
